import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { BearDecimalInput } from "@/lib/form/ui";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  CreateBreakoutValidation,
  UpdateBreakoutValidation,
  BreakoutCompositionType,
  BreakoutDimension,
  IrrigationValue,
  GMOValue,
  RiskCropProfileBreakout,
  useCreateBreakout,
  useUpdateBreakout,
  parseBreakoutValue,
} from "@/lib/api/risk/profiles/breakouts";
import { Checkbox } from "@/components/ui/checkbox";

interface BreakoutFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: string;
  customerId: string;
  commodityName: string;
  cropYear: number;
  isEdit?: boolean;
  breakout?: RiskCropProfileBreakout;
  onSuccess?: () => void;
}

type FormValues = {
  breakout_type: BreakoutCompositionType;
  single_dimension?: BreakoutDimension;
  single_value?: string;
  composite_irrigation?: IrrigationValue | "";
  composite_gmo?: GMOValue | "";
  acreage: string;
  projected_yield: string;
  actual_yield: string;
  cost_per_acre: string;
  is_enabled: boolean;
};

export function BreakoutFormDialog({
  isOpen,
  onClose,
  profileId,
  customerId,
  commodityName,
  cropYear,
  isEdit = false,
  breakout,
  onSuccess,
}: BreakoutFormDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createBreakout = useCreateBreakout(profileId);
  const updateBreakout = useUpdateBreakout();

  // Parse existing breakout data for editing
  const getDefaultValues = (): FormValues => {
    if (isEdit && breakout) {
      const criteria = parseBreakoutValue(
        breakout.breakout_type,
        breakout.breakout_value
      );

      if (breakout.breakout_type === BreakoutCompositionType.SINGLE) {
        const single = criteria as any;
        return {
          breakout_type: BreakoutCompositionType.SINGLE,
          single_dimension: single.dimension,
          single_value: single.value,
          acreage: breakout.acreage?.toString() || "",
          projected_yield: breakout.projected_yield?.toString() || "",
          actual_yield: breakout.actual_yield?.toString() || "",
          cost_per_acre: breakout.cost_per_acre?.toString() || "",
          is_enabled: breakout.is_enabled,
        };
      } else {
        const composite = criteria as any;
        return {
          breakout_type: BreakoutCompositionType.COMPOSITE,
          composite_irrigation: composite.IRRIGATION,
          composite_gmo: composite.GMO,
          acreage: breakout.acreage?.toString() || "",
          projected_yield: breakout.projected_yield?.toString() || "",
          actual_yield: breakout.actual_yield?.toString() || "",
          cost_per_acre: breakout.cost_per_acre?.toString() || "",
          is_enabled: breakout.is_enabled,
        };
      }
    }

    return {
      breakout_type: BreakoutCompositionType.SINGLE,
      single_dimension: BreakoutDimension.GMO,
      single_value: GMOValue.GMO,
      acreage: "",
      projected_yield: "",
      actual_yield: "",
      cost_per_acre: "",
      is_enabled: true,
    };
  };

  const form = useForm<FormValues>({
    defaultValues: getDefaultValues(),
  });

  const breakoutType = form.watch("breakout_type");

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      // Build the criteria based on breakout type
      let breakout_criteria: any;
      if (values.breakout_type === BreakoutCompositionType.SINGLE) {
        breakout_criteria = {
          dimension: values.single_dimension,
          value: values.single_value,
        };
      } else {
        breakout_criteria = {};
        if (values.composite_irrigation) {
          breakout_criteria[BreakoutDimension.IRRIGATION] =
            values.composite_irrigation;
        }
        if (values.composite_gmo) {
          breakout_criteria[BreakoutDimension.GMO] = values.composite_gmo;
        }
      }

      if (isEdit && breakout) {
        const updateData = {
          acreage: values.acreage,
          projected_yield: values.projected_yield,
          actual_yield: values.actual_yield,
          cost_per_acre: values.cost_per_acre,
          is_enabled: values.is_enabled,
        };
        const result = await updateBreakout(breakout.id, updateData);
        if (result.success) {
          toast({ title: "Breakout updated successfully" });
          if (onSuccess) onSuccess();
          onClose();
        } else {
          toast({ title: "Failed to update breakout" });
        }
      } else {
        const createData = {
          profile_id: profileId,
          customer_id: customerId,
          breakout_type: values.breakout_type,
          breakout_criteria,
          acreage: values.acreage,
          projected_yield: values.projected_yield,
          actual_yield: values.actual_yield,
          cost_per_acre: values.cost_per_acre,
          is_enabled: values.is_enabled,
        };
        const result = await createBreakout(createData);
        if (result.success) {
          toast({ title: "Breakout created successfully" });
          if (onSuccess) onSuccess();
          onClose();
        } else if (result.message === 'Breakout already exists') {
          toast({ title: "Breakout already exists" });
        } else {
          toast({ title: "Failed to create breakout" });
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({ title: "An error occurred while saving the breakout" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] lg:max-w-[700px] lg:min-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Breakout" : "Create New Breakout"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update breakout details for ${commodityName} (${cropYear})`
              : `Create a new breakout for ${commodityName} (${cropYear})`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Breakout Type - only shown when creating */}
            {!isEdit && (
              <FormField
                control={form.control}
                name="breakout_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Breakout Type</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={isSubmitting}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select breakout type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={BreakoutCompositionType.SINGLE}>
                            Single Dimension (e.g., GMO or Irrigation only)
                          </SelectItem>
                          <SelectItem value={BreakoutCompositionType.COMPOSITE}>
                            Composite (e.g., Irrigated GMO)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Single Dimension Options */}
            {!isEdit && breakoutType === BreakoutCompositionType.SINGLE && (
              <>
                <FormField
                  control={form.control}
                  name="single_dimension"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dimension</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select dimension" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={BreakoutDimension.GMO}>
                              GMO Status
                            </SelectItem>
                            <SelectItem value={BreakoutDimension.IRRIGATION}>
                              Irrigation Status
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch("single_dimension") ===
                  BreakoutDimension.GMO && (
                    <FormField
                      control={form.control}
                      name="single_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>GMO Status</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isSubmitting}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select GMO status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={GMOValue.GMO}>GMO</SelectItem>
                                <SelectItem value={GMOValue.NON_GMO}>Non-GMO</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                {form.watch("single_dimension") ===
                  BreakoutDimension.IRRIGATION && (
                    <FormField
                      control={form.control}
                      name="single_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Irrigation Status</FormLabel>
                          <FormControl>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                              disabled={isSubmitting}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select irrigation status" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={IrrigationValue.IRRIGATED}>Irrigated</SelectItem>
                                <SelectItem value={IrrigationValue.NON_IRRIGATED}>Non-Irrigated</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
              </>
            )}

            {/* Composite Options */}
            {!isEdit && breakoutType === BreakoutCompositionType.COMPOSITE && (
              <>
                <FormField
                  control={form.control}
                  name="composite_irrigation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Irrigation Status</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select irrigation status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={IrrigationValue.IRRIGATED}>Irrigated</SelectItem>
                            <SelectItem value={IrrigationValue.NON_IRRIGATED}>Non-Irrigated</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="composite_gmo"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GMO Status</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value || ""}
                          disabled={isSubmitting}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select GMO status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={GMOValue.GMO}>GMO</SelectItem>
                            <SelectItem value={GMOValue.NON_GMO}>Non-GMO</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {/* Common Fields */}
            <FormField
              control={form.control}
              name="acreage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acreage</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Acreage"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>
                    Total planted acres for this breakout
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="projected_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Projected Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Projected Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Expected yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="actual_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Actual Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Actual Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Realized yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cost_per_acre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost Per Acre</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Cost Per Acre"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Cost per acre for this breakout</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Enabled Status - Only shown in edit mode */}
            {isEdit && (
              <FormField
                control={form.control}
                name="is_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Show Breakout</FormLabel>
                      <FormDescription>
                        When disabled, this breakout will be hidden and ignored
                        for totaling.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isEdit ? "Update Breakout" : "Create Breakout"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
